import React, { useCallback, useRef, type ReactNode } from 'react';
import type { AnalyticsAttributes } from '@atlassian/jira-product-analytics-bridge';
import ErrorBoundary, {
	type Props as ErrorBoundaryProps,
	type ErrorBoundaryFallbackComponent,
	type ExtraErrorAttributes,
} from '@jira/platform__error-boundary/src/ErrorBoundary.tsx';
import { ErrorFlagRenderer } from '@jira/platform__error-flag-renderer/src/ErrorBoundaryFlag.tsx';
import { ReportErrors } from '@jira/platform__error-reporting/src/ReportErrors.tsx';
import { fg } from '@jira/platform__feature-gating';
import { JSErrorPageAsync } from '@jira/platform__js-error-page/src/JSErrorPageAsync.tsx';

export type JSErrorBoundaryFallbackProps = Parameters<ErrorBoundaryFallbackComponent>[0];

export interface JSErrorBoundaryProps extends ErrorBoundaryProps {
	id: string; // overriden to mandatory
	children: ReactNode; // overriden to mandatory

	fallback?: 'page' | 'flag' | 'unmount' | ErrorBoundaryFallbackComponent;
	// This one eslint-disable is better than multiple FlowFixMe(s) in all the usages where attributes is given as a function

	attributes?: AnalyticsAttributes | ((arg1: Error) => AnalyticsAttributes);
	sendToPrivacyUnsafeSplunk?: boolean;
}

const Empty: ErrorBoundaryFallbackComponent = () => null;
const getFallback = (
	fallback: JSErrorBoundaryProps['fallback'],
	ErrorFlag: ErrorBoundaryFallbackComponent,
	ErrorPageFallback: ErrorBoundaryFallbackComponent,
): ErrorBoundaryFallbackComponent | undefined => {
	switch (fallback) {
		case 'page':
			return ErrorPageFallback;
		case 'flag':
			return ErrorFlag;
		case 'unmount':
			return Empty;
		default:
			return fallback;
	}
};

const DEFAULT_EXTRA_EVENT_DATA = {};

/**
 * This component acts as a safeguard for JavaScript applications by capturing errors within child components,
 * logging these errors for further analysis,
 * and substituting the errored component tree with a customizable fallback UI.
 * It enhances error handling by allowing developers
 * to specify their own error processing logic and fallback content,
 * ensuring users are presented with a graceful degradation of service in case of errors.
 *
 * @link https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 *
 * WARNING a React error boundary does not catch all errors!
 */
export const JSErrorBoundary = ({
	id,
	packageName,
	teamName,
	fallback = 'page',
	children,
	attributes,
	sendToPrivacyUnsafeSplunk,
	onError,
	extraEventData = DEFAULT_EXTRA_EVENT_DATA,
	...rest
}: JSErrorBoundaryProps): React.JSX.Element => {
	const ErrorFlag = useCallback(
		({ error }: Parameters<ErrorBoundaryFallbackComponent>[0]) => (
			<ErrorFlagRenderer id={id} error={error} packageName={packageName} teamName={teamName} />
		),
		[id, packageName, teamName],
	);

	const ErrorPage = useCallback(
		({ error }: { error: Error }) => {
			return (
				<JSErrorPageAsync error={error} id={id} packageName={packageName} teamName={teamName} />
			);
		},
		[teamName, packageName, id],
	);

	const onErrorRef = useRef(onError);
	// oxlint-disable-next-line react/refs
	onErrorRef.current = onError;
	const onErrorHandler = useCallback(
		(location: string, error: Error, errorAttributes: ExtraErrorAttributes) => {
			onErrorRef.current?.(location, error, errorAttributes);
		},
		[],
	);

	const fallbackType: string = typeof fallback === 'function' ? 'custom' : fallback;
	return (
		<ErrorBoundary
			id={id}
			packageName={packageName}
			teamName={teamName}
			render={getFallback(fallback, ErrorFlag, ErrorPage)}
			onError={onErrorHandler}
			extraEventData={extraEventData}
			{...rest}
		>
			<ReportErrors
				id={id}
				packageName={packageName}
				teamName={teamName}
				attributes={attributes}
				sendToPrivacyUnsafeSplunk={sendToPrivacyUnsafeSplunk}
				fallbackType={fg('jserrorboundary_analytics_update') ? fallbackType : undefined}
			>
				{children}
			</ReportErrors>
		</ErrorBoundary>
	);
};
